import { ReviewTrace, ReviewVerdict } from '../../../api/structs';
import {
  ReviewProfileCheckpoint,
  ReviewProfileSlot,
  ReviewProfileSource,
} from '../../../api/structs/reviewProfile';
import { edgeStyleForKind } from '../canvas/edgeStyles';
import { edgeId } from '../canvas/graph/types';
import {
  REVIEWER_CORE_NODE_ID,
  REVIEWER_VERDICT_NODE_ID,
  REVIEW_CORE_ANY_SLOT_HANDLE,
  REVIEW_CORE_RULING_HANDLE,
  REVIEW_IN_HANDLE,
  REVIEW_SOURCE_HANDLE,
  REVIEW_VERDICT_OUTCOMES,
  ReviewerEdge,
  ReviewerEdgeKind,
  ReviewerNode,
  ReviewerSlotPort,
  ReviewerSourceLane,
  ReviewerStatus,
  reviewPolicyNodeId,
  reviewSlotHandle,
  reviewSourceNodeId,
  reviewVerdictEdgeKind,
  reviewVerdictHandle,
} from './graphTypes';
import { reviewRunEvidence } from './reviewRunEvidence';
import { reviewerLayout } from './reviewerLayout';

/**
 * Sources whose contribution is a PICTURE rather than words. They sit in the
 * lower source row so the two things the judge takes in — what it reads and
 * what it looks at — read as two flows rather than one list.
 */
const IMAGE_SOURCE_IDS = new Set([
  'candidate',
  'references',
  'scope',
  'clip',
  'similarity',
  'vision',
]);

/** Sources that carry a criterion rather than context. */
const CHECK_SOURCE_IDS = new Set(['checks', 'lint', 'similarity']);

/** References and neighbouring scenes arrive as images. */
const IMAGE_EDGE_SOURCE_IDS = new Set(['references', 'scope']);

export const reviewSourceLane = (sourceId: string): ReviewerSourceLane =>
  IMAGE_SOURCE_IDS.has(sourceId) ? 'image' : 'text';

export const reviewSourceEdgeKind = (sourceId: string): ReviewerEdgeKind => {
  if (IMAGE_EDGE_SOURCE_IDS.has(sourceId)) return 'review_image';
  if (CHECK_SOURCE_IDS.has(sourceId)) return 'review_check';
  return 'review_text';
};

const statusOf = (thing: { on: boolean; available: boolean }): ReviewerStatus => {
  if (!thing.available) return 'unavailable';
  return thing.on ? 'on' : 'off';
};

// ==================== Derivation ====================

const reviewEdge = (
  source: string,
  sourceHandle: string,
  target: string,
  targetHandle: string,
  kind: ReviewerEdgeKind,
  dsl: string,
  live: boolean
): ReviewerEdge => ({
  id: edgeId(source, sourceHandle, target, targetHandle),
  source,
  sourceHandle,
  target,
  targetHandle,
  // `derived: true` is what keeps the reviewer picture READ-ONLY: styleEdges
  // renders these through the structural edge type, which has no delete
  // affordance and is neither selectable nor deletable.
  data: { kind, dsl, derived: true },
  style: live
    ? edgeStyleForKind(kind)
    : { ...edgeStyleForKind(kind), strokeDasharray: '4 4', strokeOpacity: 0.18 },
});

/** Still slots in template order, then the slots only the pair judge asks for. */
const corePorts = (cp: ReviewProfileCheckpoint): ReviewerSlotPort[] => {
  const ports: ReviewerSlotPort[] = [];
  const seen = new Set<string>();
  const add = (pairOnly: boolean) => (slot: ReviewProfileSlot) => {
    if (seen.has(slot.name)) return;
    seen.add(slot.name);
    ports.push({
      handle: reviewSlotHandle(slot.name),
      name: slot.name,
      source: slot.source,
      filled: slot.filled,
      pairOnly,
      note: slot.note,
    });
  };
  cp.slots.forEach(add(false));
  (cp.pair_slots || []).forEach(add(true));
  return ports;
};

const sourceNode = (source: ReviewProfileSource): ReviewerNode => ({
  id: reviewSourceNodeId(source.id),
  type: 'review-source',
  position: { x: 0, y: 0 },
  data: {
    kind: 'review-source',
    source,
    status: statusOf(source),
    lane: reviewSourceLane(source.id),
    edgeKind: reviewSourceEdgeKind(source.id),
  },
});

/**
 * One checkpoint's reviewer picture as a React Flow graph: what flows into the
 * judge (one node per source, wired to the very `{{slot}}` it fills), the judge
 * itself with its tiers and gates, its verdict, and what it may then do.
 *
 * `traces` and `verdicts` are OPTIONAL run evidence. Without them the graph is
 * the capability picture — what the reviewer is configured to do. With them it
 * additionally reports what actually happened on that run: the last outcome per
 * tier on the core, and `fired`/`skipped` on the policies the run's own record
 * mentions. A policy the run says nothing about carries no badge at all, which
 * is not the same as "it did not fire".
 */
export function profileToGraph(
  cp: ReviewProfileCheckpoint,
  mode: string,
  traces?: ReviewTrace[],
  verdicts?: ReviewVerdict[]
): { nodes: ReviewerNode[]; edges: ReviewerEdge[] } {
  const evidence = reviewRunEvidence(traces, verdicts);
  const ports = corePorts(cp);
  const nodes: ReviewerNode[] = cp.sources.map(sourceNode);

  nodes.push({
    id: REVIEWER_CORE_NODE_ID,
    type: 'reviewer-core',
    position: { x: 0, y: 0 },
    data: {
      kind: 'reviewer-core',
      checkpoint: cp,
      mode,
      ports,
      tierOutcomes: evidence.tierOutcomes,
    },
  });

  nodes.push({
    id: REVIEWER_VERDICT_NODE_ID,
    type: 'review-verdict',
    position: { x: 0, y: 0 },
    data: { kind: 'review-verdict', verdicts: REVIEW_VERDICT_OUTCOMES },
  });

  cp.policies.forEach((policy) => {
    nodes.push({
      id: reviewPolicyNodeId(policy.id),
      type: 'review-policy',
      position: { x: 0, y: 0 },
      data: {
        kind: 'review-policy',
        policy,
        status: statusOf(policy),
        outcome: evidence.policies[policy.id],
      },
    });
  });

  const edges: ReviewerEdge[] = [];
  const byId = new Map(cp.sources.map((source) => [source.id, source]));
  /** Source ids at least one slot names — filled or not. */
  const named = new Set(ports.map((port) => port.source));

  ports.forEach((port) => {
    const source = byId.get(port.source);
    // An unavailable source is drawn but never wired: nothing flows out of a
    // capability that does not exist yet. An unfilled slot is a port with
    // nothing arriving at it, which is exactly what "not wired" should look
    // like.
    if (!source || !source.available || !port.filled) return;
    edges.push(
      reviewEdge(
        reviewSourceNodeId(source.id),
        REVIEW_SOURCE_HANDLE,
        REVIEWER_CORE_NODE_ID,
        port.handle,
        reviewSourceEdgeKind(source.id),
        `${source.label} → {{${port.name}}}`,
        source.on
      )
    );
  });

  cp.sources.forEach((source) => {
    if (!source.available || named.has(source.id)) return;
    edges.push(
      reviewEdge(
        reviewSourceNodeId(source.id),
        REVIEW_SOURCE_HANDLE,
        REVIEWER_CORE_NODE_ID,
        REVIEW_CORE_ANY_SLOT_HANDLE,
        reviewSourceEdgeKind(source.id),
        `${source.label} → the judge`,
        source.on
      )
    );
  });

  edges.push(
    reviewEdge(
      REVIEWER_CORE_NODE_ID,
      REVIEW_CORE_RULING_HANDLE,
      REVIEWER_VERDICT_NODE_ID,
      REVIEW_IN_HANDLE,
      'review_text',
      'the ruling',
      cp.reviewed
    )
  );

  cp.policies.forEach((policy) => {
    edges.push(
      reviewEdge(
        REVIEWER_VERDICT_NODE_ID,
        reviewVerdictHandle(String(policy.verdict)),
        reviewPolicyNodeId(policy.id),
        REVIEW_IN_HANDLE,
        reviewVerdictEdgeKind(String(policy.verdict)),
        `${policy.verdict} → ${policy.label}`,
        policy.available && policy.on
      )
    );
  });

  return { nodes: reviewerLayout(nodes, edges), edges };
}

export default profileToGraph;
